import { useQuery } from '@tanstack/react-query';
import { api, type SessionInfo } from '../lib/api.js';
import { PageHeader, SectionLabel, Card, Stat, Sparkline } from '../components/ui.js';

interface RankTrackerProps {
  session: SessionInfo;
}

interface Keyword {
  id: string;
  keyword: string;
  account: string;
  position: number;
  delta_30d: number;
  volume: number;
  difficulty: number;
  trend: number[];
}

interface RankHistoryResponse {
  keywords: Keyword[];
  generated_at: string;
}

export function RankTrackerPage({ session }: RankTrackerProps) {
  const accountId = session.accounts[0]?.account_id ?? '';

  const { data, isLoading, isError } = useQuery<RankHistoryResponse>({
    queryKey: ['rank-history', accountId],
    queryFn: () => api.get<RankHistoryResponse>(`/api/dashboard/client/rank/history?account_id=${accountId}&days=30`),
    staleTime: 5 * 60_000,
    enabled: !!accountId,
  });

  const keywords: Keyword[] = data?.keywords ?? [];

  const top10 = keywords.filter((k) => k.position <= 10).length;
  const top3 = keywords.filter((k) => k.position <= 3).length;
  const movers = keywords.filter((k) => k.delta_30d > 0).length;
  const losers = keywords.filter((k) => k.delta_30d < 0).length;

  const topMovers = [...keywords].sort((a, b) => b.delta_30d - a.delta_30d).slice(0, 5);
  const topLosers = [...keywords].sort((a, b) => a.delta_30d - b.delta_30d).slice(0, 3);

  // Aggregate avg position over 30d for the headline chart
  const avgTrend: number[] = [];
  if (keywords.length > 0) {
    const trendLength = keywords[0]!.trend.length;
    for (let i = 0; i < trendLength; i++) {
      const sum = keywords.reduce((s, k) => s + (k.trend[i] ?? 0), 0);
      avgTrend.push(sum / keywords.length);
    }
  }

  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Rank Tracker"
        title={<>Where you sit in <em className="text-cream/80">search results.</em></>}
        subtitle="Bloom checks keywords daily across DataForSEO. Movement of 2+ positions triggers a notification."
      />

      {isLoading && (
        <div className="text-sm text-cream/40">Loading rank data…</div>
      )}

      {isError && (
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
          Couldn't load rank data right now. Please refresh in a moment.
        </div>
      )}

      {!isLoading && !isError && keywords.length === 0 && (
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
          No data yet — Bloom is collecting data for this account.
        </div>
      )}

      {keywords.length > 0 && (
        <>
          <section>
            <SectionLabel>Snapshot</SectionLabel>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Stat label="Tracked" value={keywords.length} />
              <Stat label="Top 3" value={top3} hint={`of ${keywords.length}`} tone="ok" />
              <Stat label="Top 10" value={top10} hint={`of ${keywords.length}`} tone="ok" />
              <Stat label="Up / Down" value={`${movers} / ${losers}`} hint="last 30 days" tone="ok" />
            </div>
          </section>

          {avgTrend.length > 0 && (
            <section>
              <SectionLabel>Average position (30d, lower is better)</SectionLabel>
              <Card>
                <Sparkline values={avgTrend.map((v) => -v)} height={64} color="rgba(165, 243, 252, 0.7)" />
                <div className="mt-3 flex items-center justify-between text-[11px] text-cream/45 tabular-nums">
                  <span>30d ago: #{avgTrend[0]!.toFixed(1)}</span>
                  <span>today: #{avgTrend[avgTrend.length - 1]!.toFixed(1)}</span>
                </div>
              </Card>
            </section>
          )}

          <section>
            <SectionLabel>Top movers (30d)</SectionLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/80 mb-3">▲ gainers</div>
                <div className="space-y-2">
                  {topMovers.map((k) => (
                    <div key={k.id} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-cream/85 truncate">{k.keyword}</span>
                      <span className="text-emerald-300/85 tabular-nums shrink-0">+{k.delta_30d}<span className="text-cream/40 ml-2">→ #{k.position}</span></span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.22em] text-rose-300/80 mb-3">▼ losers</div>
                <div className="space-y-2">
                  {topLosers.length > 0 ? topLosers.map((k) => (
                    <div key={k.id} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-cream/85 truncate">{k.keyword}</span>
                      <span className="text-rose-300/85 tabular-nums shrink-0">{k.delta_30d}<span className="text-cream/40 ml-2">→ #{k.position}</span></span>
                    </div>
                  )) : <div className="text-cream/45 text-sm">No losers — everything held or improved.</div>}
                </div>
              </Card>
            </div>
          </section>

          <section>
            <SectionLabel>All keywords</SectionLabel>
            <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-cream/40 border-b border-cream/[0.06]">
                    <th className="px-5 py-3 font-medium">Keyword</th>
                    <th className="px-3 py-3 font-medium">Location</th>
                    <th className="px-3 py-3 font-medium text-right">Volume</th>
                    <th className="px-3 py-3 font-medium text-right">KD</th>
                    <th className="px-3 py-3 font-medium text-right">30d</th>
                    <th className="px-5 py-3 font-medium text-right">Position</th>
                  </tr>
                </thead>
                <tbody>
                  {keywords.map((k) => (
                    <tr key={k.id} className="border-b border-cream/[0.04] last:border-0 hover:bg-cream/[0.02] transition-colors">
                      <td className="px-5 py-3 text-cream/85">{k.keyword}</td>
                      <td className="px-3 py-3 text-cream/65 text-[12px]">{k.account}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-cream/70">{k.volume.toLocaleString()}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-cream/70">{k.difficulty}</td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        <span className={k.delta_30d > 0 ? 'text-emerald-300/85' : k.delta_30d < 0 ? 'text-rose-300/85' : 'text-cream/40'}>
                          {k.delta_30d > 0 ? `+${k.delta_30d}` : k.delta_30d || '—'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-cream font-medium">#{k.position}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
